import { Injectable, inject } from '@angular/core';
import {
  CanActivateChild,
  ActivatedRouteSnapshot,
  Router,
  UrlTree,
  RouterStateSnapshot,
  CanActivate,
} from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { ContextService } from '../services/context.service';

@Injectable({ providedIn: 'root' })
export class EditionGuard implements CanActivate, CanActivateChild {
  private router = inject(Router);
  private contextService = inject(ContextService);
  private platformId = inject(PLATFORM_ID);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.canActivateChild(route, state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    if (!isPlatformBrowser(this.platformId)) return true;

    const currentUrl = state.url;
    const pathOnly = currentUrl.split('?')[0].split('#')[0];

    // 🔥 HARD BYPASS FOR ROOT (ABSOLUTE)
    if (pathOnly === '' || pathOnly === '/') {
      return true;
    }

    // A page opened directly (link, bookmark, crawler) is served at the URL asked for and becomes the
    // preferred edition; redirecting it by a stored preference made crawlers see /enterprise/x and /x
    // as duplicate pages.
    if (!this.router.navigated && pathOnly !== '/enterprise' && pathOnly !== '/enterprise/') {
      this.contextService.set(pathOnly.startsWith('/enterprise') ? 'enterprise' : 'saas');
      return true;
    }

    let preferred = this.contextService.get();
    if (!preferred) {
      preferred = pathOnly.startsWith('/enterprise') ? 'enterprise' : 'saas';

      this.contextService.set(preferred);
    }

    // 🔥 handle enterprise root
    if (pathOnly === '/enterprise' || pathOnly === '/enterprise/') {
      return this.router.createUrlTree(['/sitemap'], {
        queryParams: { context: 'enterprise' },
      });
    }

    const isEnterpriseUrl = pathOnly.startsWith('/enterprise');

    if (preferred === 'enterprise' && !isEnterpriseUrl) {
      return this.router.parseUrl(`/enterprise${currentUrl}`);
    }

    if (preferred === 'saas' && isEnterpriseUrl) {
      const newUrl = currentUrl.replace(/^\/enterprise/, '') || '/';
      return this.router.parseUrl(newUrl);
    }

    return true;
  }
}
